import { useState } from "react";
import { useLang } from "../i18n";
import { IMG } from "../content/images";
import { PageHero } from "../components/PageHero";
import { Seo } from "../components/Seo";
import { Turnstile } from "../components/Turnstile";
import { Field, Input, Textarea, Select, Consent, Honeypot, FormStatus } from "../components/forms";
import { SOLUTIONS } from "../content/solutions";
import { api, apiError } from "../lib/api";
import { ArrowLeft, ArrowRight } from "lucide-react";

const CONDITIONAL_KEYS = {
  "fibre-optique": "fibre",
  "internet-fixe-sans-fil": "wireless",
  "internet-satellite": "satellite",
  "ip-transit-dia": "iptransit",
  "genie-civil-fibre": "civil",
};

const Coverage = () => {
  const { lang, t, p } = useLang();
  const f = t.coverage.fields;
  const [form, setForm] = useState({
    customer_type: "", full_name: "", company: "", phone: "", email: "",
    province: "", city: "", commune: "", address: "", map_pin: "",
    building_type: "", users_or_sites: "", service: "", capacity: "", contact_method: "", details: "",
  });
  const [consent, setConsent] = useState(false);
  const [token, setToken] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({ ok: false, msg: "" });
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.customer_type) errs.customer_type = t.forms.required;
    if (form.full_name.trim().length < 2) errs.full_name = t.forms.required;
    if (form.phone.trim().length < 6) errs.phone = t.forms.required;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = t.forms.invalidEmail;
    if (form.address.trim().length < 3) errs.address = t.forms.required;
    if (!consent) errs.consent = t.forms.consentRequired;
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true); setStatus({ ok: false, msg: "" });
    try {
      const res = await api.post("/public/coverage", { ...form, locale: lang, consent, turnstile_token: token, honeypot });
      setStatus({ ok: true, msg: res.data.message || t.coverage.successMsg });
      setForm({ customer_type: "", full_name: "", company: "", phone: "", email: "", province: "", city: "", commune: "", address: "", map_pin: "", building_type: "", users_or_sites: "", service: "", capacity: "", contact_method: "", details: "" });
      setConsent(false);
    } catch (err) { setStatus({ ok: false, msg: apiError(err, t.forms.error) }); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="coverage-page">
      <Seo title={t.nav.coverage} path="verifier-la-couverture" />
      <PageHero eyebrow="BULA TELECOMS SA" title={t.coverage.title} sub={t.coverage.subtitle}
        image={IMG.towerSunset} crumbs={[{ label: t.nav.coverage }]} />

      <section className="bg-[#F5F5F3]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <form onSubmit={submit} className="bg-white border border-[#101214]/10 rounded-2xl p-7 sm:p-10 space-y-5" data-testid="coverage-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label={f.customerType} required error={errors.customer_type} htmlFor="cv-type">
                <Select id="cv-type" data-testid="coverage-customer-type" value={form.customer_type} onChange={set("customer_type")}>
                  <option value="">—</option>
                  {t.coverage.customerTypes.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </Field>
              <Field label={f.fullName} required error={errors.full_name} htmlFor="cv-name">
                <Input id="cv-name" data-testid="coverage-name-input" value={form.full_name} onChange={set("full_name")} />
              </Field>
              <Field label={f.company} htmlFor="cv-company">
                <Input id="cv-company" data-testid="coverage-company-input" value={form.company} onChange={set("company")} />
              </Field>
              <Field label={f.phone} required error={errors.phone} htmlFor="cv-phone">
                <Input id="cv-phone" data-testid="coverage-phone-input" value={form.phone} onChange={set("phone")} placeholder="+243 …" />
              </Field>
              <Field label={f.email} required error={errors.email} htmlFor="cv-email">
                <Input id="cv-email" type="email" data-testid="coverage-email-input" value={form.email} onChange={set("email")} />
              </Field>
              <Field label={f.province} htmlFor="cv-province">
                <Input id="cv-province" data-testid="coverage-province-input" value={form.province} onChange={set("province")} placeholder="Kinshasa, Kongo-Central…" />
              </Field>
              <Field label={f.city} htmlFor="cv-city">
                <Input id="cv-city" data-testid="coverage-city-input" value={form.city} onChange={set("city")} />
              </Field>
              <Field label={f.commune} htmlFor="cv-commune">
                <Input id="cv-commune" data-testid="coverage-commune-input" value={form.commune} onChange={set("commune")} />
              </Field>
            </div>
            <Field label={f.address} required error={errors.address} htmlFor="cv-address">
              <Textarea id="cv-address" rows={2} data-testid="coverage-check-input-address" value={form.address} onChange={set("address")} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label={f.mapPin} htmlFor="cv-pin">
                <Input id="cv-pin" data-testid="coverage-pin-input" value={form.map_pin} onChange={set("map_pin")} placeholder="-4.32, 15.31" />
              </Field>
              <Field label={f.buildingType} htmlFor="cv-building">
                <Select id="cv-building" data-testid="coverage-building-type" value={form.building_type} onChange={set("building_type")}>
                  <option value="">—</option>
                  {t.coverage.buildingTypes.map((b) => <option key={b} value={b}>{b}</option>)}
                </Select>
              </Field>
              <Field label={f.usersOrSites} htmlFor="cv-users">
                <Input id="cv-users" data-testid="coverage-users-input" value={form.users_or_sites} onChange={set("users_or_sites")} />
              </Field>
              <Field label={f.service} htmlFor="cv-service">
                <Select id="cv-service" data-testid="coverage-service-select" value={form.service} onChange={set("service")}>
                  <option value="">—</option>
                  {SOLUTIONS.map((s) => <option key={s.slug} value={s[lang].title}>{s[lang].title}</option>)}
                </Select>
              </Field>
              <Field label={f.capacity} htmlFor="cv-capacity">
                <Input id="cv-capacity" data-testid="coverage-capacity-input" value={form.capacity} onChange={set("capacity")} placeholder="100 Mbps, 1 Gbps…" />
              </Field>
              <Field label={f.contactMethod} htmlFor="cv-method">
                <Select id="cv-method" data-testid="coverage-contact-method" value={form.contact_method} onChange={set("contact_method")}>
                  <option value="">—</option>
                  {t.coverage.contactMethods.map((m) => <option key={m} value={m}>{m}</option>)}
                </Select>
              </Field>
            </div>
            <Field label={f.details} htmlFor="cv-details">
              <Textarea id="cv-details" data-testid="coverage-details-input" value={form.details} onChange={set("details")} />
            </Field>
            <Consent checked={consent} onChange={setConsent} error={errors.consent} testid="coverage-consent" />
            <Turnstile action="coverage" onToken={setToken} />
            <p className="text-xs text-[#101214]/45">{t.forms.privacyNote}</p>
            <FormStatus ok={status.ok} msg={status.msg} testid="coverage-form-status" />
            <button type="submit" disabled={busy} data-testid="coverage-check-submit-button"
              className="w-full sm:w-auto inline-flex justify-center items-center gap-2 bg-[#FF6600] hover:bg-[#FF9900] disabled:opacity-50 text-[#08090A] font-black rounded-full px-8 py-3.5 text-sm transition-colors shadow-[0_0_20px_rgba(255,102,0,0.3)]">
              {busy ? t.forms.sending : t.coverage.submit} <ArrowRight size={16} />
            </button>
            <Honeypot value={honeypot} onChange={setHoneypot} />
          </form>
        </div>
      </section>
    </div>
  );
};

export default Coverage;
